/**
 * caretReminder — "which reminder is my cursor on RIGHT NOW?", so the agenda
 * can open at that reminder and keep it marked while the cursor stays there.
 *
 * Walter 2026-10-09: *"It would be really helpful if the agenda view opened to
 * the item that my cursor is currently at. I do this quite a lot and then have
 * to look for that item, which is a little tedious."* Then, on the first cut
 * (a 1.8s flash): *"I think it's better if it [shows] indefinitely and only if
 * I'm on that line right now."*
 *
 * So this is LIVE state, not a one-shot: the editor sets it as the caret moves
 * onto a 🔔 line and CLEARS it the moment the caret moves to anything else or
 * the editor goes away. Subscribers (BoardPage → AgendaPanel) re-render.
 *
 * Why the editor pushes it here instead of the agenda reading the caret when
 * it opens: by then the agenda button has blurred the editor and there is no
 * caret left to read.
 */

let caret = null;            // { listId, lineText, owner } | null
const listeners = new Set();

function notify() {
  for (const cb of listeners) {
    try { cb(caret); } catch { /* a bad subscriber must not break the editor */ }
  }
}

/**
 * setCaretLine(next) — `{ listId, lineText, owner }` when the caret sits on a
 * reminder line, or null when it does not. `owner` identifies the editor
 * instance so it can clear its own entry on unmount without stepping on
 * another editor's (board tiles stay mounted behind an open list).
 *
 * No-ops when nothing actually changed, so typing inside one reminder line
 * doesn't wake every subscriber on each keystroke.
 */
export function setCaretLine(next) {
  const valid = next && next.listId && typeof next.lineText === 'string' ? next : null;
  const same = (!valid && !caret)
    || (valid && caret
      && caret.listId === valid.listId
      && caret.lineText === valid.lineText
      // Normalised: an absent owner and a stored null are the same thing,
      // or re-reporting the same line would fire a change on every keystroke.
      && (caret.owner ?? null) === (valid.owner ?? null));
  if (same) return;
  caret = valid ? { listId: valid.listId, lineText: valid.lineText, owner: valid.owner ?? null } : null;
  notify();
}

/** Clear, but only if `owner` is the editor that set it (unmount path). */
export function clearCaretLineFor(owner) {
  if (caret && caret.owner === owner) setCaretLine(null);
}

export function clearCaretLine() {
  setCaretLine(null);
}

export function getCaretLine() {
  return caret;
}

export function subscribeCaretLine(cb) {
  if (typeof cb !== 'function') return () => {};
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/**
 * findReminderIdForLine(caretLine, reminders, hashLine) → id | null
 *
 * `hashLine` is injected (hooks/useReminders.hashReminderLine) so this module
 * stays free of Firebase and can be unit-tested.
 *
 * The match is the capture engine's own: a reminder belongs to a line when it
 * came from that list and its stored hash equals the hash of the line with the
 * 🔔 / [DONE] prefix stripped.
 */
const BELL_PREFIX_RE = /^\s*(?:🔔|\[DONE\]|🔕|~~🔔)↻?\s?/;

/** The task half of a canonical bell line: everything after the "<date>: ". */
function taskTextOf(body) {
  const sep = body.indexOf(': ');
  return (sep > 0 ? body.slice(sep + 2) : body).trim().toLowerCase();
}

export function findReminderIdForLine(caretLine, reminders, hashLine) {
  if (!caretLine || !Array.isArray(reminders) || typeof hashLine !== 'function') return null;
  const { listId, lineText } = caretLine;
  if (!listId || typeof lineText !== 'string') return null;
  const m = lineText.match(BELL_PREFIX_RE);
  if (!m) return null;
  const body = lineText.slice(m[0].length);
  if (!body.trim()) return null;
  const mine = reminders.filter(r => r && r.sourceListId === listId);

  // 1. The fingerprint the capture engine keeps. Right whenever line and
  //    reminder are in step, which is the normal case.
  let hash = null;
  try { hash = hashLine(body); } catch { hash = null; }
  if (hash) {
    const hit = mine.find(r => r.sourceLineHash === hash);
    if (hit) return hit.id;
  }

  // 2. Walter 2026-10-09: they can fall OUT of step. His `book squash` line
  //    sat against a reminder reading `book squash. small change` - the
  //    popover edit updated the reminder while the matching write to the line
  //    never landed (Ghostery was blocking Firestore writes in that window).
  //    A drifted pair is still obviously the same reminder to a human, so
  //    fall back to the task text. Only ever when exactly ONE candidate
  //    matches: two reminders reading the same thing is a real possibility,
  //    and marking the wrong row is worse than marking none.
  const task = taskTextOf(body);
  if (!task) return null;
  const exact = mine.filter(r => String(r.text ?? '').trim().toLowerCase() === task);
  if (exact.length === 1) return exact[0].id;

  // 3. One is a prefix of the other - an edit on one side only, which is
  //    precisely the drift above.
  const partial = mine.filter(r => {
    const rt = String(r.text ?? '').trim().toLowerCase();
    if (!rt) return false;
    return rt.startsWith(task) || task.startsWith(rt);
  });
  return partial.length === 1 ? partial[0].id : null;
}

/** Convenience: resolve whatever the caret is on at this moment. */
export function resolveCaretReminderId(reminders, hashLine) {
  return findReminderIdForLine(caret, reminders, hashLine);
}

/** The text after the 🔔 / [DONE] prefix — what the reminder hash is taken over. */
export function bellBody(lineText) {
  const s = String(lineText ?? '');
  const m = s.match(BELL_PREFIX_RE);
  return m ? s.slice(m[0].length) : null;
}

export function isBellLine(lineText) {
  return BELL_PREFIX_RE.test(String(lineText ?? ''));
}
